import React, { createContext, useContext, useState, useEffect } from "react"
import { useUser } from "./UserContext"

import sendAnalyticsEvent from "../utils/analytics/sendAnalyticsEvent"
import getTrackingId from "../utils/cookie/getTrackingId"
import { clampPriceDecimal } from "../utils/convertNumbers"

export type CartItem = MergedProductData & {
  cartItemId: string
  variantIndex: number
  quantity: number
}

interface CartContextType {
  cartOpen: boolean
  openCart: () => void
  closeCart: () => void

  cartItems: CartItem[]
  addToCart: (product: MergedProductData, variantIndex: number) => void
  removeItemFromCart: (cartItemId: string) => void
  adjustQuantityOfCartItem: (cartItemId: string, delta: number) => void

  proceedToCheckout: () => Promise<void>
}

const CartContext = createContext<CartContextType>({
  cartOpen: false,
  openCart: null,
  closeCart: null,

  cartItems: [],
  addToCart: null,
  removeItemFromCart: null,
  adjustQuantityOfCartItem: null,

  proceedToCheckout: null,
})

const SHOPPING_BAG_VERSION = "1.0.0"

export const CartProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const {
    loading,
    data: { id: userId, email, firstName, lastName, testGroupIndex },
  } = useUser()
  const [loadingCart, setLoadingCart] = useState(true)

  const [cartOpen, setCartOpen] = useState(false)
  const [cartItems, setCartItems] = useState<CartItem[]>([])

  const openCart = () => setCartOpen(true)
  const closeCart = () => setCartOpen(false)

  // Load cart from localStorage on mount
  useEffect(() => {
    if (!loading) {
      const storedCart = localStorage.getItem("shoppingBag")
      const storedCartVersion = localStorage.getItem("shoppingBagVersion")

      if (storedCart && storedCartVersion != SHOPPING_BAG_VERSION) {
        localStorage.removeItem("shoppingBag")
        localStorage.setItem("shoppingBagVersion", SHOPPING_BAG_VERSION)
      } else {
        if (storedCart) {
          setCartItems(JSON.parse(storedCart))
        }
      }

      setLoadingCart(false)
    }
  }, [loading])

  // Save cart to localStorage
  useEffect(() => {
    if (!loadingCart) {
      localStorage.setItem("shoppingBag", JSON.stringify(cartItems))
      localStorage.setItem("shoppingBagVersion", SHOPPING_BAG_VERSION)
      // localStorage.removeItem("bigCommerceCheckoutUrl")
    }
  }, [cartItems, loadingCart])

  const sendAddToCartAnalyticsEvent = async (cartItem: MergedProductData) => {
    try {
      sendAnalyticsEvent({
        type: "add_to_cart",
        first_name: firstName,
        last_name: lastName,
        email,
        test_group_index: testGroupIndex,

        product_data: [
          {
            id: cartItem.bigcommerceProductOriginalId.toString(),
            name: cartItem.title,
            slug: cartItem.slug,
            value: clampPriceDecimal(cartItem.price),
            quantity: 1,
          },
        ],
      })
    } catch (error) {
      console.error(error)
    }
  }

  // Add item to cart
  const addToCart = (product: MergedProductData, variantIndex: number) => {
    sendAddToCartAnalyticsEvent(product)

    localStorage.removeItem("bigCommerceCheckoutUrl")

    const variant = product.variants[variantIndex]

    const uniqueId =
      product.bigcommerceProductOriginalId +
      (variant.id ? `-${variant.id}` : "")

    setCartItems(prev => {
      const existingItem = prev.find(item => item.cartItemId == uniqueId)
      const newCart = [...prev]

      if (existingItem) {
        newCart.forEach(item => {
          if (item.cartItemId == uniqueId) {
            item.quantity += 1
          }
        })
      } else {
        newCart.push({
          ...product,
          cartItemId: uniqueId,
          variantIndex,
          quantity: 1,
        })
      }

      return newCart
    })

    openCart()
  }

  // Remove item from cart
  const removeItemFromCart = (cartItemId: string) => {
    localStorage.removeItem("bigCommerceCheckoutUrl")

    setCartItems(prev => {
      const newCart = prev.filter(item => item.cartItemId !== cartItemId)
      return newCart
    })
  }

  // Adjust quantity
  const adjustQuantityOfCartItem = (cartItemId: string, delta: number) => {
    localStorage.removeItem("bigCommerceCheckoutUrl")

    setCartItems(prev => {
      const newCart = [...prev]

      newCart.forEach(item => {
        if (item.cartItemId == cartItemId) {
          item.quantity += delta
        }
      })

      for (let i = newCart.length - 1; i >= 0; i--) {
        const cartItem = newCart[i]
        if (cartItem.quantity <= 0) {
          newCart.splice(i, 1)
        }
      }

      return newCart
    })
  }

  // Proceed to checkout
  const proceedToCheckout = async () => {
    const storeToken = localStorage.getItem("STORE_TOKEN") || ""
    const cartId = localStorage.getItem("CART_ID")

    const bigCommerceCheckoutUrl = localStorage.getItem(
      "bigCommerceCheckoutUrl",
    )
    if (bigCommerceCheckoutUrl) {
      window.location.href = "https://checkout.theroyalkellys.com/checkout"
      return
    }

    // Prepare the line items from the local cart
    const lineItems = cartItems
      .map(item => ({
        product_id: item.bigcommerceProductOriginalId,
        quantity: item.quantity,
        variant_id: item.variants[item.variantIndex]?.id,
      }))
      .filter(item => item.variant_id !== undefined) // Remove items without variant_id if required

    try {
      // Set up checkout tracking
      const trackingRequestBody = {
        first_name: firstName,
        last_name: lastName,
        email,
        test_group_index: testGroupIndex,

        product_data: cartItems.map(cartItem => ({
          id: cartItem.bigcommerceProductOriginalId.toString(),
          name: cartItem.title,
          slug: cartItem.slug,
          value: clampPriceDecimal(cartItem.price),
          quantity: cartItem.quantity,
        })),

        tracking_id: getTrackingId(),
        source_url: window.location.href,
      }

      const completeCheckoutTrackingRequestBody = JSON.stringify({
        ...trackingRequestBody,
        type: "complete_checkout",
      })
      document.cookie = `complete_checkout_tracking_request_body=${completeCheckoutTrackingRequestBody};path=/;domain=${window.location.hostname}`

      const [response] = await Promise.all([
        // Create BigCommerce cart for checkout
        fetch("/.netlify/functions/create-or-update-cart", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${storeToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(
            userId
              ? { customer_id: userId, line_items: lineItems, cart_id: cartId }
              : { line_items: lineItems, cart_id: cartId },
          ),
        }),

        // Send initiate checkout analytics event
        sendAnalyticsEvent({
          ...trackingRequestBody,
          type: "initiate_checkout",
        }),
      ])
      const data = await response.json()

      if (data.error) throw new Error(data.error)

      const { checkout_url, cart_id } = data

      localStorage.setItem("CART_ID", cart_id)

      if (!checkout_url) {
        throw new Error("Failed to create cart or retrieve checkout URL")
      }

      localStorage.setItem("bigCommerceCheckoutUrl", checkout_url)
      window.location.href = checkout_url
    } catch (error) {
      console.error("❌ Error proceeding to checkout:", error)
    }
  }

  return (
    <CartContext.Provider
      value={{
        cartOpen,
        openCart,
        closeCart,
        cartItems,
        addToCart,
        removeItemFromCart,
        adjustQuantityOfCartItem,
        proceedToCheckout,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  return useContext(CartContext)
}
